import * as React from 'react';

import {
  Badge,
  ComponentType,
  Form,
  Grid,
  IBreadcrumbItem,
  IconName,
  Scrollbar,
  convertISOStringToLocalDateString,
  IPagedResultsV1,
  useForm,
} from '@gcl/react-core';
import { createDefaultDetailPageTitleFn, DetailPageLayout } from '@app/DetailPageLayout';
import { DetailPageRightMenu } from '@common/detailPageRightMenu/DetailPageRightMenu';
import { routes } from '@routes/routes.models';
import { EDetailPageRightMenuGeneralInfoType } from '@common/detailPageRightMenu/detailPageRightMenuGeneralInfo.models';
import { Tab, Tabs } from '@core/Tabs/Tab';
import {
  useFetchLoadingRemovalCert,
  useFetchLoadingRemovalCertT3,
} from '@permitToLoadRemove/permitToLoadRemove.queries';
import { getAccessMode } from '@common/detailPage.hooks';
import { useNavigate } from '@routes/routes.hooks';
import { useProjectId } from '@config/config.hooks';
import { DiscussionList } from '@discussion/DiscussionList';
import { EProjectDiscussionFormType } from '@discussion/projectDiscussion/projectDiscussion.constants';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { EProjectDiscussionFileType } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.constants';
import { useSearchParams } from 'react-router-dom';
import { ESelectT1CreationModalSearchParamKey } from '@common/SelectT1CreationModal.constants';
import { getMasterScheduleDetail } from '@masterSchedule/masterSchedule.hooks';
import { Attachments } from '@file/Attachments';
import { IProjectFileAttachmentsLoadingRemovalCertsRequest } from '@file/projectFile/projectFile.models';
import { useFetchAttachmentLoadingRemovalCerts } from '@file/projectFile/projectFile.queries';
import { DEFAULT_SELECT_PAGE_SIZE, FETCH_QUERIES_NAME } from '@common/constants';
import { ESelectRevisionModalType } from '@common/SelectRevisionModal.constants';
import { useSelectRevisionListModal } from '@common/SelectRevisionModal.hooks';
import { LabelWithMessage } from '@common/LabelWithMessage';
import usePrompt from '@common/prompt.hooks';
import { EPermitToLoadStatus } from '@permitToLoadRemove/permitToLoadRemove.models';
import { isRestrictedAccessFromApiResponse } from '@common/api.services';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { IProjectDrawingRegisterSearch } from '@drawing/projectDrawingRegister.models';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { useQuery } from '@tanstack/react-query';
import { ECreateT4FormKeys } from './permitToLoadRemoveDetailPage.constants';
import { PermitToLoadRemoveDetailTabDetail } from './PermitToLoadRemoveDetailTabDetail';
import { isSupersededT4, useT4CommentPermission, useT4DetailIsReadOnly } from './permitToLoadRemovalDetailPage.hooks';
import { IProjectLoadingRemovalCert, IProjectLoadingRemovalCertFormState } from './permitToLoadRemoveDetailPage.models';

interface IPermitToLoadRemoveDetailPageProps {}

export const PermitToLoadRemoveDetailPage: React.FunctionComponent<IPermitToLoadRemoveDetailPageProps> = () => {
  const { id, mode } = getAccessMode();

  const [searchParams] = useSearchParams();
  const searchParamMasterScheduleId =
    searchParams.get(ESelectT1CreationModalSearchParamKey.masterScheduleId) !== null
      ? parseInt(searchParams.get(ESelectT1CreationModalSearchParamKey.masterScheduleId) ?? '0', 10)
      : undefined;

  const { navigate, projectNo } = useNavigate();

  const projectId = useProjectId();

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const {
    formState: { isDirty },
  } = useFormMethods;

  const {
    data: designModifications,
    isLoading: isLoadingT3,
    isRefetching: isRefetchingT3,
    error: errorDesignModifications,
  } = useFetchLoadingRemovalCertT3({ projectId }, id, !isDirty);
  const {
    data: loadingRemovalCert,
    isLoading: isLoadingLoadingRemovalCert,
    isRefetching: isRefetchingLoadingRemovalCert,
    error: errorFetchLoadingRemovalCert,
  } = useFetchLoadingRemovalCert({ projectId }, id, !isDirty);
  const {
    masterSchedule,
    isInitialLoading: isLoadingGetMasterSchedule,
    isRefetching: isRefetchingGetMasterSchedule,
    error: errorGetMasterSchedule,
  } = getMasterScheduleDetail(
    projectId,
    loadingRemovalCert?.projectMasterScheduleId ?? searchParamMasterScheduleId,
    !isDirty && (mode === 'Create' ? !!searchParamMasterScheduleId : !!loadingRemovalCert?.projectMasterScheduleId),
  );
  const {
    hasCommentRight,
    hasResponseRight,
    isReadOnly: isCommentReadOnly,
    canViewDraftComment,
  } = useT4CommentPermission(masterSchedule);

  const {
    openPortal,
    renderModal: renderRevisionListModal,
    badge: revisionBadge,
  } = useSelectRevisionListModal({
    type: ESelectRevisionModalType.T4,
    revisions: loadingRemovalCert?.projectRevisionCerts,
    currentT4: loadingRemovalCert,
  });
  const { data: defaultReleasedForConstructionData, isLoading: isLoadingDefaultReleasedForConstructionData } = useQuery<
    IPagedResultsV1<IProjectDrawingRegisterSearch>
  >(
    [
      projectId,
      FETCH_QUERIES_NAME.DRAWING_SEARCH,
      { hasRfcDrawingOnly: true, masterScheduleId: masterSchedule?.id, drawingTitle: undefined },
      undefined,
      undefined,
      DEFAULT_SELECT_PAGE_SIZE,
      1,
    ],
    { enabled: false },
  );

  React.useEffect(() => {
    if (!useFormMethods.formState.isDirty)
      useFormMethods.reset(getDefaultFormValues(mode, loadingRemovalCert, defaultReleasedForConstructionData?.items));
  }, [
    JSON.stringify([loadingRemovalCert, defaultReleasedForConstructionData, mode]),
    useFormMethods.formState.isDirty,
  ]);

  const getDefaultFormValues = (
    modeLocal: DetailPageAccessMode,
    loadingRemovalCertLocal?: IProjectLoadingRemovalCert,
    defaultReleasedForConstructionDataLocal?: IProjectDrawingRegisterSearch[],
  ) => {
    const permitToLoadRemark = loadingRemovalCertLocal?.isRemovalCert
      ? loadingRemovalCertLocal?.projectLoadCert?.twcComment
      : loadingRemovalCertLocal?.twcComment;
    const permitToRemoveRemark = loadingRemovalCertLocal?.isRemovalCert ? loadingRemovalCertLocal?.twcComment : null;
    const permitToLoadRemarkIce = loadingRemovalCertLocal?.isRemovalCert
      ? loadingRemovalCertLocal?.projectLoadCert?.iceComment
      : loadingRemovalCertLocal?.iceComment;
    const permitToRemoveRemarkIce = loadingRemovalCertLocal?.isRemovalCert
      ? loadingRemovalCertLocal?.iceComment
      : undefined;

    const selectedDrawings =
      modeLocal === 'Create'
        ? (defaultReleasedForConstructionDataLocal ?? []).map((item) => {
            const key = item.latestRfcHistory?.id ?? -1;
            return { data: item, key, text: `${item.drawingNo} - ${item.documentDrawingTitle}` };
          })
        : (loadingRemovalCertLocal?.project_LoadingRemovalCertDrawing || []).map((d) => {
            const { history, ...rest } = d.projectDrawingRegister;
            return {
              data: {
                ...rest,
                latestRfcHistory: { ...history },
              },
              key: d.projectDrawingRegisterHistoryId,
              text: `${d.projectDrawingRegister.drawingNo} - ${d.projectDrawingRegister.documentDrawingTitle}`,
            };
          });

    const state: IProjectLoadingRemovalCertFormState = {
      [ECreateT4FormKeys.LOCATION]: loadingRemovalCertLocal?.location ?? null,
      [ECreateT4FormKeys.ELEMENT_OF_WORKS]: loadingRemovalCertLocal?.elementOfWorks ?? null,
      [ECreateT4FormKeys.EXTENT_OF_OPERATION_AND_CONSTRUCTION_ACTIVITY]:
        loadingRemovalCertLocal?.operationAndConstructionActivity ?? null,
      [ECreateT4FormKeys.PERMIT_VALID_UNTIL]: loadingRemovalCertLocal?.certificateValidDateTime
        ? convertISOStringToLocalDateString(loadingRemovalCertLocal?.certificateValidDateTime ?? '')
        : '',
      [ECreateT4FormKeys.IS_FINAL_REMOVAL_CERT]: loadingRemovalCertLocal?.isFinalRemovalCert ?? false,
      [ECreateT4FormKeys.PERMIT_TO_REMOVE_REQUIRED]: loadingRemovalCertLocal?.removalCertRequired ?? false,
      [ECreateT4FormKeys.PERMIT_TO_LOAD_REMARK]: permitToLoadRemark ?? null,
      [ECreateT4FormKeys.PERMIT_TO_REMOVE_REMARK]: permitToRemoveRemark ?? null,
      [ECreateT4FormKeys.PERMIT_TO_LOAD_REMARK_ICE]: permitToLoadRemarkIce ?? null,
      [ECreateT4FormKeys.PERMIT_TO_REMOVE_REMARK_ICE]: permitToRemoveRemarkIce ?? null,
      [ECreateT4FormKeys.RELEASED_FOR_CONSTRUCTION_DRAWINGS]: selectedDrawings,
      [ECreateT4FormKeys.SELECTED_TWCS]: [],
    };

    return state;
  };

  // const isRestricted = checkGeneralAccessRestricted({
  //   designerCompanyId: masterSchedule?.responsibleDesigner?.id,
  //   checkerCompanyId: masterSchedule?.responsibleChecker.id,
  // });

  const isRestricted = React.useMemo(() => {
    return isRestrictedAccessFromApiResponse([errorFetchLoadingRemovalCert, errorGetMasterSchedule]);
  }, [JSON.stringify(errorFetchLoadingRemovalCert), JSON.stringify(errorGetMasterSchedule)]);

  const isLoading =
    mode === 'Create'
      ? isLoadingGetMasterSchedule
      : isLoadingGetMasterSchedule || isLoadingLoadingRemovalCert || isLoadingT3;
  const isRefetching =
    mode === 'Create'
      ? isRefetchingGetMasterSchedule
      : isRefetchingGetMasterSchedule || isRefetchingLoadingRemovalCert || isRefetchingT3;

  const isDetailIsReadOnly = useT4DetailIsReadOnly(loadingRemovalCert, masterSchedule);
  const isReadOnly = mode === 'View' || isDetailIsReadOnly;

  const isSupersededLocal = isSupersededT4(loadingRemovalCert);

  const breadcrumbItems: IBreadcrumbItem[] = [
    {
      text: `${projectNo} - Home`,
      prefixIconName: 'House',
      onClick: () => {
        navigate(routes.t1.path);
      },
    },
    {
      text: 'Search T4',
      prefixIconName: 'Search' as IconName,
      onClick: () => {
        navigate(routes.t4.path);
      },
    },
    {
      text: `${mode} T4`,
      active: true,
      prefixIconName: 'JournalText' as IconName,
    },
  ];

  const tabs = React.useMemo(() => {
    const tempTabs = [
      <Tab headerText="Details" key="gcl-gems-t4-tab-detail">
        <Form
          defaultValues={{
            callback: () => {
              const state = getDefaultFormValues(mode, loadingRemovalCert, defaultReleasedForConstructionData?.items);
              return state;
            },
            deps: [
              {
                loadingRemovalCert,
                defaultReleasedForConstructionData: defaultReleasedForConstructionData?.items,
                mode,
              },
            ],
          }}
          enableUnsavedChangesPrompt
          unsavedChangesPromptProps={{
            usePrompt,
          }}
          useFormMethods={useFormMethods}
          render={(formFieldMethods) => (
            <PermitToLoadRemoveDetailTabDetail
              mode={mode}
              isReadOnly={isReadOnly}
              designModifications={designModifications || []}
              loadingRemovalCertDetail={loadingRemovalCert}
              loadingRemovalCertId={id}
              masterScheduleId={loadingRemovalCert?.projectMasterScheduleId ?? searchParamMasterScheduleId}
              masterSchedule={masterSchedule}
              formFieldMethods={formFieldMethods}
              useFormMethods={useFormMethods}
              isSuperseded={isSupersededLocal}
            />
          )}
        />
      </Tab>,
    ];

    if (loadingRemovalCert?.iceSignatureRequired)
      tempTabs.push(
        <Tab headerText="ICE Cert" key="gcl-gems-t4-tab-ice-cert">
          <LabelWithMessage labelFor={ECreateT4FormKeys.ICE_CERT} text="ICE Cert" />
          <Attachments<IProjectFileAttachmentsLoadingRemovalCertsRequest>
            fileType={EProjectFileType.CONSTRUCTION_CHECK_CERT}
            mode={mode}
            id={ECreateT4FormKeys.ICE_CERT}
            uploadFileRequestParam={{ loadingRemovalCertId: id }}
            useFetchFilesQueryKey={[
              projectId,
              id,
              FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_LOADING_REMOVAL_CERTS,
              EProjectFileType.CONSTRUCTION_CHECK_CERT,
            ]}
            useFetchFiles={useFetchAttachmentLoadingRemovalCerts}
            useFetchFilesProps={{
              loadingRemovalCertId: id ?? 0,
              projectId,
              fileType: EProjectFileType.CONSTRUCTION_CHECK_CERT,
            }}
          />
        </Tab>,
      );

    if (id) {
      tempTabs.push(
        <Tab headerText="Comment" key="gcl-gems-t4-tab-comment">
          <DiscussionList
            isReadOnly={isCommentReadOnly}
            formType={EProjectDiscussionFormType.LOADING_REMOVAL_CERT}
            projectLoadingRemovalCertId={id ?? 0}
            fileFormType={EProjectFileType.LOADING_REMOVAL_CERT_DISCUSSION_ATTACHMENT}
            discussionFileType={EProjectDiscussionFileType.LOADING_REMOVAL_CERT}
            canComment={hasCommentRight}
            canResponse={hasResponseRight}
            canViewDraftComment={canViewDraftComment}
          />
        </Tab>,
      );
    }
    return tempTabs;
  }, [
    id,
    JSON.stringify(loadingRemovalCert),
    JSON.stringify(useFormMethods.getValues()),
    JSON.stringify(useFormMethods.formState.errors),
    JSON.stringify(masterSchedule),
    hasCommentRight,
    hasResponseRight,
    isCommentReadOnly,
    mode,
  ]);

  return (
    <Scrollbar>
      <ApiErrorDisplay
        errors={[
          { error: errorDesignModifications, genericError: 'Get design modification failed' },
          { error: errorFetchLoadingRemovalCert, genericError: 'Get T4 failed' },
          { error: errorGetMasterSchedule, genericError: 'Get master Schedule failed' },
        ]}
      />
      <DetailPageLayout
        errorGetDetail={errorFetchLoadingRemovalCert}
        isRestricted={isRestricted}
        data={mode === 'Create' ? 'Place Holder' : loadingRemovalCert}
        isLoading={isLoading}
        isRefetching={isRefetching}
        rightMenu={(setIsDrawerOpen) => (
          <DetailPageRightMenu
            setIsDrawerOpen={setIsDrawerOpen}
            type={EDetailPageRightMenuGeneralInfoType.T4}
            masterSchedule={masterSchedule}
            loadingRemovalCert={loadingRemovalCert}
          />
        )}
        title={createDefaultDetailPageTitleFn(
          <>
            {`T4 Permit to Load / Remove ${
              mode !== 'Create' && loadingRemovalCert?.certificateNo ? ` - ${loadingRemovalCert?.certificateNo}` : ''
            }`}
            {revisionBadge}
            {loadingRemovalCert?.status === EPermitToLoadStatus.CANCEL && (
              <Badge pill type={ComponentType.Secondary} className="gcl-ml-sm gcl-fb-md gcl-fc-greyscale-100 ">
                Cancelled
              </Badge>
            )}
          </>,
        )}
        breadcrumb={breadcrumbItems}
        render={(DetailPageRightMenuOpener) => {
          return (
            <>
              <Grid container>
                <Grid item xs={12}>
                  <Tabs>{tabs}</Tabs>
                </Grid>
              </Grid>
              {renderRevisionListModal()}
            </>
          );
        }}
      />
    </Scrollbar>
  );
};
